// Which of the three client tiers (ADR-0015) a given view can actually link
// to. One rule, one module, because getting it wrong renders a dead link.
//
// Desk / Ops / Comply expose a Contacts and a Suitability Records route tree
// (`router.tsx`, `frontends.ts`); View exposes neither. That is not merely a
// missing sidebar entry — `Contact.personas` does not include `view` at all.
//
// The reason this must be a route check and not a "can the caller read it"
// inference: `personaReadVerdict` short-circuits to `allow` for any actor whose
// `users` row grants the `admin` token (`registry.ts`, `actor.ts`), and the
// all-personas admin account exists in every environment. Such a user, active
// as View, DOES resolve a Contact — so a "the server returned a contact,
// therefore the link is safe" assumption produces a link to a route that does
// not exist. Ask this module instead.

/** The sibling tier detail pages a given view exposes. A tier link renders
 * only when its base path is present here. There is no `clients` entry: the
 * Clients tier has no detail page in any view — an Account is read on its
 * client's Client Book record. */
export interface TriTierLinkTargets {
    /** Every staff view has a Client Book record page — and it is where an
     * Account's own identity is read, so this link is always present. */
    clientBook: string;
    suitabilityRecords?: string;
    contacts?: string;
}

export function triTierLinkTargets(viewPrefix: string): TriTierLinkTargets {
    const clientBook = `/${viewPrefix}/client-book`;
    switch (viewPrefix) {
        case 'desk':
        case 'ops':
        case 'comply':
            return {
                clientBook,
                suitabilityRecords: `/${viewPrefix}/suitability-records`,
                contacts: `/${viewPrefix}/contacts`,
            };
        default:
            // View: the client record only — it exposes neither sibling tier
            // page, and cannot even read Contacts.
            return { clientBook };
    }
}

/** The href of a Contact detail page in this view, or null when the view has
 * no Contacts route to land on. */
export function contactHref(viewPrefix: string, contactId: string): string | null {
    const base = triTierLinkTargets(viewPrefix).contacts;
    return base ? `${base}/${contactId}` : null;
}

/** The href of a Suitability Record detail page in this view, or null. */
export function suitabilityRecordHref(viewPrefix: string, recordId: string): string | null {
    const base = triTierLinkTargets(viewPrefix).suitabilityRecords;
    return base ? `${base}/${recordId}` : null;
}

/**
 * Where a Client (Account) endpoint opens (SEP-188): its person Client's
 * Client Book record — the Clients tier has no detail page of its own, so this
 * takes the CLIENT id, never the Account id. Null when the server resolved no
 * single readable owner (a joint Account, or one outside the caller's read),
 * which is the whole reason the caller must pass a nullable id through rather
 * than build the path itself.
 *
 * Unlike its two siblings this never returns null for want of a route: every
 * staff view has a Client Book (`TriTierLinkTargets.clientBook`).
 */
export function clientHref(viewPrefix: string, clientId: string | null): string | null {
    if (!clientId) return null;
    return `${triTierLinkTargets(viewPrefix).clientBook}/${clientId}`;
}
